import {produce, enableMapSet} from "immer";
import {getDefaultSessionUsageForModel} from "../../Globals.ts";

export interface QueuedMessage {
    id: string;
    text: string;
    files: Record<string, any>;
    timestamp: number;
    isImportant: boolean;
}

// Enable the MapSet plugin for Immer
enableMapSet();

interface DynamicStateObj {
    shareMessages: any;
    shareMessagesSequence: number[];
    shareChatName: string;
    importantMessageIds: Set<string>;
    isCurrentPromptImportant: boolean;
    playingId: any;
    applyingCodeBlockId: string | null;
    openFileStatuses: any[];
    additionalIdeContext: any;
    selectedAdditionalIdeContext: any;
    canSubmit: boolean;
    editingId: any;
    errorMessage: null | string;
    errorPopupMessage: null | string;
    hasAttachment: boolean;
    hasText: boolean;
    selectedMedia: any;
    isMenuOpen: boolean;
    isUploadOpen: boolean;
    focusedFile: null | string;
    visiblePopup: null | string;
    enlargedContent: null | any;
    chatHistory: any[];
    isInitializing: boolean;
    scrollToBottom: boolean;
    conversationId: string | null;
    elementsInProgress: any;
    sessionUsage: any;
    isStopping: boolean;
    recordingType: 'speech' | 'audio' | null;
    mediaItemsInProgress: string[];
    unreviewedFileTracker: any;
    projectFileBatchIds: any;
    embeddingInfo: {
        status: 'active' | 'error' | null;
        error: string | null;
        progress: number;
    };
    messageQueue: QueuedMessage[];
}

export const dynamicStateObjSlice = (set: any) => ({
    dynamicStateObj: {
        shareMessages: null,
        shareMessagesSequence: [],
        shareChatName: "New Chat",
        importantMessageIds: new Set(),
        isCurrentPromptImportant: false,
        playingId: null,
        applyingCodeBlockId: null,
        openFileStatuses: [],
        additionalIdeContext: {files: [], folders: []},
        selectedAdditionalIdeContext: {files: [], folders: []},
        canSubmit: true,
        editingId: null,
        errorMessage: null,
        errorPopupMessage: null,
        hasText: false,
        hasAttachment: false,
        selectedMedia: {},
        isMenuOpen: false,
        isUploadOpen: false,
        focusedFile: null,
        visiblePopup: null,
        enlargedContent: null,
        chatHistory: [],
        isInitializing: true,
        scrollToBottom: false,
        conversationId: null,
        elementsInProgress: new Set(),
        sessionUsage: {sessionStart: Date.now()},
        isStopping: false,
        recordingType: null,
        mediaItemsInProgress: [],
        unreviewedFileTracker: {},
        projectFileBatchIds: {},
        embeddingInfo: {
            status: null,
            error: null,
            progress: 0
        },
        messageQueue: []
    },

    setDynamicStateObj: (newDynamicStateObj: Partial<DynamicStateObj>) => set(produce((state: any) => {
        Object.assign(state.dynamicStateObj, newDynamicStateObj);
    })),

    updateElementInProgress: (element: any, operation: string) => set(produce((state: any) => {
        if (operation === "add") state.dynamicStateObj.elementsInProgress.add(element);
        else if (operation === "delete") state.dynamicStateObj.elementsInProgress.delete(element);
        else if (operation === "abort") {
            state.dynamicStateObj.elementsInProgress.clear();
            state.dynamicStateObj.elementsInProgress.add('abort');
        }
        else state.dynamicStateObj.elementsInProgress.clear();
    })),

    updateSelectedMedia: (key: string, val: any, operation: string) => set(produce((state: any) => {
        if (operation === "add") {
            state.dynamicStateObj.hasAttachment = true;
            state.dynamicStateObj.selectedMedia[key] = val;
            state.dynamicStateObj.mediaItemsInProgress.push(key);
        }
        else if (operation === "delete") {
            delete state.dynamicStateObj.selectedMedia[key];
            state.dynamicStateObj.hasAttachment = Object.keys(state.dynamicStateObj.selectedMedia).length;
            state.dynamicStateObj.mediaItemsInProgress = state.dynamicStateObj.mediaItemsInProgress.filter((item: string) => item !== key);
        }
        else if (operation === "update") {
            if (state.dynamicStateObj.selectedMedia[key]) {
                Object.entries(val).forEach(([field, value]) => {
                    if (val.loadingInfo.task === 'done') {
                        state.dynamicStateObj.mediaItemsInProgress = state.dynamicStateObj.mediaItemsInProgress.filter((item: string) => item !== key);
                    }
                    state.dynamicStateObj.selectedMedia[key][field] = value;
                });
            }
        }
        else {
            state.dynamicStateObj.selectedMedia = {};
            state.dynamicStateObj.hasAttachment = false;
            state.dynamicStateObj.mediaItemsInProgress = [];
        }
    })),

    updateChatHistory: (element: any, operation: string) => set(produce((state: any) => {
        if (operation === "add") state.dynamicStateObj.chatHistory.push(element);
        else if (operation === "delete") state.dynamicStateObj.chatHistory = state.dynamicStateObj.chatHistory.filter((chat: any) => chat.id !== element);
        else if (operation === "edit") state.dynamicStateObj.chatHistory = state.dynamicStateObj.chatHistory.map((chat: any) => chat.id === element.id ? {...chat, name: element.newName} : chat)
        else if (operation === "toggleFavorite") state.dynamicStateObj.chatHistory = state.dynamicStateObj.chatHistory.map((chat: any) => chat.id === element.id ? {...chat, isFavorite: element.isFavorite} : chat)
        else state.dynamicStateObj.chatHistory = [];
    })),

    updateUnreviewedFileTracker: (filePath: string, element: any, operation: string) => set(produce((state: any) => {
        if (operation === "add") state.dynamicStateObj.unreviewedFileTracker[filePath] = element;
        else if (operation === "delete") delete state.dynamicStateObj.unreviewedFileTracker[filePath];
        else if (operation === "clear") state.dynamicStateObj.unreviewedFileTracker = {};
    })),

    updateImportantMessageIds: (element: any, operation: string) => set(produce((state: any) => {
        if (operation === "add") state.dynamicStateObj.importantMessageIds.add(element);
        else if (operation === "delete") state.dynamicStateObj.importantMessageIds.delete(element);
        else state.dynamicStateObj.importantMessageIds.clear();
    })),

    updateProjectFileBatchIds: (key: any, operation: string) => set(produce((state: any) => {
        if (operation === "add") state.dynamicStateObj.projectFileBatchIds[key] = false;
        else if (operation === "delete") state.dynamicStateObj.projectFileBatchIds[key] = true;
        else if (operation === "clear") state.dynamicStateObj.projectFileBatchIds = {};
    })),

    updateSessionUsage: (element: any, operation: string) => set(produce((state: any) => {
        if (operation === "update") {
            if (!state.dynamicStateObj.sessionUsage[element.model]) {
                state.dynamicStateObj.sessionUsage[element.model] = getDefaultSessionUsageForModel();
            }

            const modelUsage = state.dynamicStateObj.sessionUsage[element.model];

            modelUsage.input_tokens += element.input_tokens;
            modelUsage.output_tokens += element.output_tokens;
            modelUsage.input_audio_tokens += element.input_audio_tokens;
            modelUsage.output_audio_tokens += element.output_audio_tokens;
            modelUsage.reasoning_tokens += element.reasoning_tokens;
            modelUsage.cache_creation_input_tokens += element.cache_creation_input_tokens;
            modelUsage.cache_read_input_tokens += element.cache_read_input_tokens;
            modelUsage.total_tokens += element.total_tokens;

            modelUsage.input_charges += element.input_charges;
            modelUsage.output_charges += element.output_charges;
            modelUsage.input_audio_charges += element.input_audio_charges;
            modelUsage.output_audio_charges += element.output_audio_charges;
            modelUsage.reasoning_charges += element.reasoning_charges;
            modelUsage.cache_creation_input_charges += element.cache_creation_input_charges;
            modelUsage.cache_read_input_charges += element.cache_read_input_charges;
            modelUsage.total_charges += element.total_charges;
        }
        else if (operation === "clear") {
            state.dynamicStateObj.sessionUsage = {sessionStart: Date.now()};
        }
    })),

    updateSelectedAdditionalIdeContext: (element: { item: string, type: 'files' | 'folders', isSelected?: boolean }, operation: string) => set(produce((state: any) => {
        if (operation === "add") {
            if (!state.dynamicStateObj.selectedAdditionalIdeContext[element.type]) {
                state.dynamicStateObj.selectedAdditionalIdeContext[element.type] = [];
            }
            state.dynamicStateObj.selectedAdditionalIdeContext[element.type].push({
                filePath: element.item,
                isSelected: element.isSelected ?? true
            });
        }
        else if (operation === "delete") {
            state.dynamicStateObj.selectedAdditionalIdeContext[element.type] =
                state.dynamicStateObj.selectedAdditionalIdeContext[element.type]?.filter(
                    (i: any) => i.filePath !== element.item
                ) || [];
        }
        else if (operation === "toggle") {
            const items = state.dynamicStateObj.selectedAdditionalIdeContext[element.type];
            const index = items?.findIndex((i: any) => i.filePath === element.item);
            if (index !== -1 && items) {
                items[index].isSelected = !items[index].isSelected;
            }
        }
        else {
            state.dynamicStateObj.selectedAdditionalIdeContext = {files: [], folders: []};
        }
    })),

    updateOpenFileStatuses: (element: { filePath: string, isSelected: boolean }, operation: string) => set(produce((state: any) => {
        if (operation === "toggle") {
            state.dynamicStateObj.openFileStatuses = state.dynamicStateObj.openFileStatuses.map((fileStatus: any) => {
                if (fileStatus.filePath === element.filePath) {
                    return { ...fileStatus, isSelected: element.isSelected };
                }
                return fileStatus;
            });
        }
        else if (operation === "add") {
            state.dynamicStateObj.openFileStatuses.push({
                filePath: element.filePath,
                isSelected: element.isSelected
            });
        }
        else if (operation === "delete") {
            state.dynamicStateObj.openFileStatuses = state.dynamicStateObj.openFileStatuses.filter(
                (fileStatus: any) => fileStatus.filePath !== element.filePath
            );
        }
        else {
            state.dynamicStateObj.openFileStatuses = [];
        }
    })),

    updateMessageQueue: (element: any, operation: string) => set(produce((state: any) => {
        if (operation === "add") {
            state.dynamicStateObj.messageQueue.push(element);
        }
        else if (operation === "remove") {
            state.dynamicStateObj.messageQueue = state.dynamicStateObj.messageQueue.filter((msg: QueuedMessage) => msg.id !== element);
        }
        else if (operation === "reorder") {
            const { fromIndex, toIndex } = element;
            if (fromIndex >= 0 && fromIndex < state.dynamicStateObj.messageQueue.length &&
                toIndex >= 0 && toIndex < state.dynamicStateObj.messageQueue.length) {
                const [movedMessage] = state.dynamicStateObj.messageQueue.splice(fromIndex, 1);
                state.dynamicStateObj.messageQueue.splice(toIndex, 0, movedMessage);
            }
        }
        else if (operation === "clear") {
            state.dynamicStateObj.messageQueue = [];
        }
    }))
});